import type { EmployeeSnapshot, SelfServicePatch } from '@klukka/client';
import {
  FUNDS,
  fmt,
  fmtDate,
  rates,
  t,
  validateEmployeeSelfService,
  type LangCode,
} from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useState } from 'react';

type Tr = (k: Parameters<typeof t>[1]) => string;

interface FormState {
  phone: string;
  email: string;
  address: string;
  bank: string;
  union: string;
  pensionFund: string;
  creditUtil: string;
  priorUnusedCredit: string;
  extraOn: boolean;
  extraPensionPct: number;
  extraPensionFund: string;
}

/**
 * Self-service profile.
 *
 * Identity and pay are employer-owned and shown locked; the worker owns their
 * contact details, bank account, fund choices and tax-credit settings. Splitting
 * it this way is what makes onboarding cheap: the employer enters a name and a
 * wage, the worker fills in the rest in their own language.
 */
export function ProfileTab({ snap, lang, busy, onSave, onLogout }: {
  snap: EmployeeSnapshot;
  lang: LangCode;
  busy: boolean;
  onSave: (patch: SelfServicePatch) => Promise<void>;
  onLogout: () => void;
}) {
  const tr: Tr = (k) => t(lang, k);
  const emp = snap.employee;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [tip, setTip] = useState<string | null>(null);

  const start = () => {
    setForm({
      phone: emp.phone,
      email: emp.email,
      address: emp.address,
      bank: emp.bank,
      union: emp.union,
      pensionFund: emp.pensionFund,
      creditUtil: String(emp.creditUtil),
      priorUnusedCredit: String(emp.priorUnusedCredit),
      extraOn: emp.extraPensionPct > 0,
      extraPensionPct: emp.extraPensionPct || 2,
      extraPensionFund: emp.extraPensionFund ?? FUNDS.sereign[0],
    });
    setErrs({});
    setTip(null);
    setEditing(true);
  };

  const save = async () => {
    if (!form) return;
    const found = validateEmployeeSelfService(form);
    if (Object.keys(found).length) { setErrs(found); return; }
    await onSave({
      phone: form.phone,
      email: form.email,
      address: form.address,
      bank: form.bank,
      union: form.union,
      pensionFund: form.pensionFund,
      creditUtil: parseFloat(form.creditUtil) || 0,
      priorUnusedCredit: parseFloat(form.priorUnusedCredit) || 0,
      extraPensionPct: form.extraOn ? form.extraPensionPct : 0,
      extraPensionFund: form.extraOn ? form.extraPensionFund : null,
    });
    setEditing(false);
  };

  if (!editing || !form) {
    const hourly = rates(snap.company.settings, emp).dv;
    return (
      <div className="ph-body">
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {tr('myInfo')}
            <button className="edit-btn" onClick={start}>
              <Ic.edit size={14} /> <span style={{ fontSize: 12, fontWeight: 600 }}>{tr('editBtn')}</span>
            </button>
          </div>

          <div className="rowline"><span>{tr('nameLbl')}</span><b>{emp.name}</b></div>
          <div className="rowline"><span>kt.</span><b className="mono">{emp.kennitala}</b></div>
          <div className="rowline"><span>{tr('startDate')}</span><b>{fmtDate(emp.startDate)}</b></div>
          <div className="rowline"><span>{tr('jobTitle')}</span><b>{emp.role || '—'}</b></div>
          <div className="rowline">
            <span>{tr('wageBasis')}</span>
            <b>
              {fmt(emp.monthlyWage)}/mán.{' '}
              <span className="dimtx" style={{ fontWeight: 400, fontSize: 12 }}>· {fmt(hourly)}/klst</span>
            </b>
          </div>
          <div className="dimtx" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, margin: '2px 0 4px' }}>
            <Ic.lock size={12} /> {tr('setByEmployer')}
          </div>

          <div className="rowline"><span>{tr('mobile')}</span><b>{emp.phone || '—'}</b></div>
          <div className="rowline"><span>Email</span><b>{emp.email || '—'}</b></div>
          <div className="rowline"><span>{tr('address')}</span><b>{emp.address || '—'}</b></div>
          <div className="rowline"><span>{tr('bank')}</span><b className="mono" style={{ fontSize: 13 }}>{emp.bank || '—'}</b></div>
          <div className="rowline top-sep"><span>{tr('union')}</span><b>{emp.union}</b></div>
          <div className="rowline"><span>{tr('pensionFund')}</span><b>{emp.pensionFund}</b></div>
          <div className="rowline">
            <span>{tr('sereign')}</span>
            <b>{emp.extraPensionPct ? `${emp.extraPensionPct}% · ${emp.extraPensionFund ?? '—'}` : '—'}</b>
          </div>
          <div className="rowline"><span>{tr('taxDiscount')}</span><b>{emp.creditUtil}%</b></div>
          <div className="rowline"><span>{tr('priorUnused')}</span><b>{fmt(emp.priorUnusedCredit)}</b></div>
        </div>

        <button className="btn-ghost" onClick={onLogout}><Ic.logout size={15} /> {tr('logout')}</button>
      </div>
    );
  }

  const set = (k: keyof FormState) => (e: { target: { value: string } }) => {
    setForm({ ...form, [k]: e.target.value });
    if (errs[k]) setErrs({ ...errs, [k]: '' });
  };

  const field = (opts: {
    k: keyof FormState;
    label: string;
    tipKey?: Parameters<typeof t>[1];
    link?: string;
    type?: string;
    placeholder?: string;
    options?: string[];
    half?: boolean;
  }) => (
    <label className="fld" style={opts.half ? undefined : { width: '100%' }}>
      <span className="fld-head">
        {opts.label}
        {opts.tipKey ? (
          <button type="button" className="tip-btn" onClick={() => setTip(tip === opts.k ? null : opts.k)} aria-label={tr('moreInfo')}>
            <Ic.info size={14} />
          </button>
        ) : null}
      </span>
      {opts.tipKey && tip === opts.k ? (
        <span className="tooltip">
          {tr(opts.tipKey)}
          {opts.link ? <a href={opts.link} target="_blank" rel="noreferrer noopener">{tr('moreInfo')} ↗</a> : null}
        </span>
      ) : null}
      {opts.options ? (
        <select className="inp" value={String(form[opts.k])} onChange={set(opts.k)}>
          {opts.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className={`inp${errs[opts.k] ? ' err' : ''}`}
          type={opts.type ?? 'text'}
          placeholder={opts.placeholder}
          value={String(form[opts.k])}
          onChange={set(opts.k)}
        />
      )}
      {errs[opts.k] ? <span className="err-msg">{tr('errFormat')}: {errs[opts.k]}</span> : null}
    </label>
  );

  return (
    <div className="ph-body">
      <div className="card">
        <div className="card-title">{tr('myInfo')} — {tr('editBtn')}</div>
        <div className="dimtx" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
          <Ic.lock size={12} /> {emp.name} · {emp.kennitala} · {emp.role || '—'} · {tr('setByEmployer')}
        </div>

        <div className="split">
          {field({ k: 'phone', label: tr('mobile'), half: true })}
          {field({ k: 'email', label: 'Email', half: true })}
        </div>
        {field({ k: 'address', label: tr('address') })}
        {field({ k: 'bank', label: tr('bank'), tipKey: 'tipBank', placeholder: '0000-26-000000' })}

        <div className="split">
          {field({ k: 'union', label: tr('union'), tipKey: 'tipUnion', link: 'https://www.efling.is', options: FUNDS.unions, half: true })}
          {field({ k: 'pensionFund', label: tr('pensionFund'), tipKey: 'tipPension', link: 'https://www.lifeyrismal.is', options: FUNDS.pension, half: true })}
        </div>

        <div className="accordion">
          <label className="acc-head">
            <input type="checkbox" checked={form.extraOn} onChange={(e) => setForm({ ...form, extraOn: e.target.checked })} />
            <span>{tr('sereign')}</span>
            <button type="button" className="tip-btn" onClick={() => setTip(tip === 'sereign' ? null : 'sereign')} aria-label={tr('moreInfo')}>
              <Ic.info size={14} />
            </button>
          </label>
          {tip === 'sereign' ? (
            <span className="tooltip" style={{ margin: '6px 0' }}>
              {tr('sereignTip')}
              <a href="https://www.lifeyrismal.is/is/sereignarsparnadur" target="_blank" rel="noreferrer noopener">{tr('moreInfo')} ↗</a>
            </span>
          ) : null}
          {form.extraOn ? (
            <div>
              <div className="fld" style={{ marginTop: 6 }}>
                {tr('contribLbl')}
                <div className="seg3" style={{ marginTop: 4 }}>
                  {[2, 4].map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={form.extraPensionPct === p ? 'on' : ''}
                      onClick={() => setForm({ ...form, extraPensionPct: p })}
                    >
                      {p}% (+2%)
                    </button>
                  ))}
                </div>
              </div>
              {field({ k: 'extraPensionFund', label: tr('fundLbl'), options: FUNDS.sereign })}
            </div>
          ) : null}
        </div>

        <div className="split">
          {field({ k: 'creditUtil', label: tr('taxDiscount'), tipKey: 'tipCredit', link: 'https://island.is/personuafslattur-og-skattthrep-launatekna', type: 'number', half: true })}
          {field({ k: 'priorUnusedCredit', label: tr('priorUnused'), tipKey: 'tipPrior', link: 'https://www.skatturinn.is/einstaklingar/stadgreidsla/', type: 'number', half: true })}
        </div>

        <div className="sheet-btns">
          <button className="btn-ghost" onClick={() => setEditing(false)}>{tr('cancel')}</button>
          <button className="btn-main" style={{ flex: 1 }} disabled={busy} onClick={() => void save()}>{tr('saveBtn')}</button>
        </div>
      </div>
    </div>
  );
}
